// ─── Flujo piloto, fase 2: la requisición (RQ) ───────────────────────────────
// Lo que el pedido (la OP) le pide a compras. Como en el panel de ventas:
//   · Pide SOLO lo que falta. De la mercancía, lo que está «sin pedir» en
//     Material en transición (no está en bodega ni comprada ni pedida); de los
//     servicios (marcación, transporte…), los costos que todavía no tienen
//     orden de compra ni cuenta por pagar.
//   · Una RQ por pedido. Si después falta algo más, se «pone al día»: se le
//     agregan renglones; los que ya se ordenaron no se tocan.
//   · Cuánto de un renglón ya se ordenó NO se guarda: se deriva. De la
//     mercancía, sumando las líneas de OCP que citan el renglón (`_rqLinea`);
//     de un servicio, mirando si su costo ya tiene OCP o CxP. Así la RQ nunca
//     dice algo distinto de los documentos.
//   · Lo que se compró POR FUERA del piloto (una OCP hecha con el botón de
//     siempre) también cuenta: Material en transición lo ve como comprado, y
//     el renglón de la RQ deja de pedirlo. Nunca se compra dos veces.
//
// Es puro: los documentos llegan en `ctx` (como en cadenaPedido.js).

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
  .toLowerCase().replace(/\s+/g, " ").trim();
const num = (v) => { const n = Number(String(v ?? "").replace(/,/g, ".")); return Number.isFinite(n) ? n : 0; };

export const rqsDeOP = (opId, ctx) => (ctx.requisiciones || []).filter(r => r.pedido_id === opId && r.estado !== "anulada");

// Lo ya ordenado de un renglón, y en qué documentos.
export function ordenadoDeLinea(linea, op, ctx) {
  if (linea.tipo === "servicio") {
    const c = ((op && op._conceptosCosto) || []).find(x => x._ccid && x._ccid === linea.ccid);
    const ocp = c && c._ocpId ? (ctx.ocp || []).find(o => o.id === c._ocpId && o.estado !== "cancelada") : null;
    const cxp = c && c._cxpId ? (ctx.cuentas_pagar || []).find(x => x.id === c._cxpId) : null;
    const doc = ocp ? { tipo: "OCP", id: ocp.id, numero: ocp.numero } : cxp ? { tipo: "CXP", id: cxp.id, numero: cxp.numero } : null;
    return { ordenado: doc ? num(linea.cantidad) : 0, docs: doc ? [doc] : [], costoBorrado: !c };
  }
  const docs = [];
  let ordenado = 0;
  (ctx.ocp || []).filter(o => o.estado !== "cancelada").forEach(o => {
    const cant = (o.items || []).filter(it => it._rqLinea && it._rqLinea === linea.lid).reduce((s, it) => s + num(it.cantidad), 0);
    if (cant > 0) { ordenado += cant; docs.push({ tipo: "OCP", id: o.id, numero: o.numero, cantidad: cant }); }
  });
  return { ordenado, docs };
}

export function saldoLinea(linea, op, ctx) {
  const { ordenado, docs, costoBorrado } = ordenadoDeLinea(linea, op, ctx);
  const cantidad = num(linea.cantidad);
  const pendiente = linea.cerrada || costoBorrado ? 0 : Math.max(0, cantidad - ordenado);
  const estado = linea.cerrada ? "cerrada" : costoBorrado ? "sin_costo"
    : pendiente <= 0 ? "ordenada" : ordenado > 0 ? "parcial" : "pendiente";
  return { cantidad, ordenado, pendiente, docs, estado };
}

// Para Material en transición: cuánto de un artículo de esta OP está pedido a
// compras y todavía no se ha ordenado.
export function rqPendienteFn(ctx) {
  return (op, articulo) => rqsDeOP(op.id, ctx).reduce((s, rq) => s + (rq.items || [])
    .filter(l => l.tipo === "mercancia" && norm(l.articulo) === norm(articulo))
    .reduce((t, l) => t + saldoLinea(l, op, ctx).pendiente, 0), 0);
}

// ─── Qué le falta pedir a esta OP ───────────────────────────────────────────
// `cad` es su cadena y `filas` las de Material en transición (calculadas CON
// lo que ya está en sus RQ, para no pedir dos veces).
export function faltantesParaRQ(cad, filas, ctx) {
  const op = cad.op;
  const mias = (filas || []).filter(f => f.cad.op.id === op.id && f.sinPedir > 0);
  const merc = new Map();
  mias.forEach(f => {
    const origen = f.c.esParte ? f.linea.articulo : "";
    const k = norm(f.c.articulo) + "|" + norm(origen);
    const e = merc.get(k) || { tipo: "mercancia", articulo: f.c.articulo, articulo_origen: origen, cantidad: 0, costo: num(f.costo) };
    e.cantidad += f.sinPedir;
    merc.set(k, e);
  });
  // Un servicio que ya está en la RQ no se vuelve a pedir, tampoco si se cerró
  // («no se necesita» es una decisión, no un olvido).
  const yaPedidos = new Set(rqsDeOP(op.id, ctx).flatMap(rq => (rq.items || []).filter(l => l.tipo === "servicio").map(l => l.ccid)));
  const servicios = (cad.servicios || [])
    .filter(s => s.ccid && s.sinDocumento && s.pendiente && s.via !== "nada" && !yaPedidos.has(s.ccid))
    .map(s => ({ tipo: "servicio", ccid: s.ccid, concepto: s.concepto || s.articulo, articulo: s.articulo || "", articulo_origen: s.parte || "",
      cantidad: s.cantidad || 1, costo: s.valor, proveedor_nombre: s.tercero || "", via: s.via || "" }));
  return [...merc.values(), ...servicios];
}

// ─── Lo que falta por ordenar, de todas las RQ ──────────────────────────────
// Cada renglón con saldo. De la mercancía, el saldo se recorta a lo que
// Material en transición todavía ve «pedido a compras» para esa OP y artículo:
// si mientras tanto llegó a la bodega o se compró por fuera, ya no hace falta.
export function lineasPorOrdenar(ops, filas, ctx) {
  const opPorId = new Map((ops || []).map(o => [o.id, o]));
  const cupo = new Map();
  (filas || []).forEach(f => {
    const k = f.cad.op.id + "|" + norm(f.c.articulo);
    cupo.set(k, (cupo.get(k) || 0) + f.enRQ);
  });
  const salida = [];
  (ctx.requisiciones || []).filter(r => r.estado !== "anulada")
    .slice().sort((a, b) => (a.fecha || "").localeCompare(b.fecha || "") || String(a.numero || "").localeCompare(String(b.numero || "")))
    .forEach(rq => {
      const op = opPorId.get(rq.pedido_id);
      if (!op || op.estado === "cancelada") return;
      (rq.items || []).forEach(l => {
        const s = saldoLinea(l, op, ctx);
        if (s.pendiente <= 0) return;
        let pendiente = s.pendiente, porFuera = 0;
        if (l.tipo === "mercancia") {
          const k = op.id + "|" + norm(l.articulo);
          const disponible = cupo.has(k) ? cupo.get(k) : 0;
          pendiente = Math.min(s.pendiente, disponible);
          cupo.set(k, disponible - pendiente);
          porFuera = s.pendiente - pendiente;
          if (pendiente <= 0) return;
        }
        salida.push({ rq, op, linea: l, saldo: s, pendiente, porFuera });
      });
    });
  return salida;
}

// ─── Armar las órdenes de lo seleccionado ───────────────────────────────────
// La mercancía se agrupa por OP y proveedor: una OCP de AURA cuelga de UNA
// orden de producción (su `_origen`), y de eso dependen la liquidación, el
// Control de Órdenes y el detalle de la OP. Mezclar pedidos en una OCP, como
// hace el panel, los rompería. Los servicios no se agrupan aquí: se le pone el
// tercero y el destino al costo y lo generan las sincronizaciones de siempre.
export function agruparOrdenes(seleccion) {
  const grupos = new Map();
  const servicios = [];
  const errores = [];
  (seleccion || []).forEach(s => {
    const cant = num(s.cantidad);
    if (s.linea.tipo === "servicio") {
      if (!(s.proveedor_nombre || "").trim()) errores.push(`${s.linea.concepto || "Servicio"} (${s.op.numero}): falta el tercero`);
      else if (s.via !== "ocp" && s.via !== "cxp") errores.push(`${s.linea.concepto || "Servicio"} (${s.op.numero}): elige si va a orden de compra o a cuenta de cobro`);
      else servicios.push(s);
      return;
    }
    if (!s.proveedor) { errores.push(`${s.linea.articulo} (${s.op.numero}): falta el proveedor`); return; }
    if (cant <= 0) { errores.push(`${s.linea.articulo} (${s.op.numero}): la cantidad debe ser mayor que cero`); return; }
    const k = s.op.id + "|" + s.proveedor.id;
    const g = grupos.get(k) || { op: s.op, proveedor: s.proveedor, items: [] };
    const precio = num(s.costo);
    g.items.push({
      articulo: s.linea.articulo, descripcion: s.linea.articulo_origen ? `Parte de ${s.linea.articulo_origen}` : "",
      color: "", logo: "", impresion: "", ubicacion: "",
      cantidad: cant, precio_unitario: precio, total: cant * precio, _rqLinea: s.linea.lid
    });
    grupos.set(k, g);
  });
  return { ocps: [...grupos.values()], servicios, errores };
}
